import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { AuthService } from './auth.service';

type PermissionBag = Record<string, boolean>;

@Injectable({
  providedIn: 'root',
})
export class LotteryAccessService {
  private accountCache: any | null = null;
  private accountPromise: Promise<any> | null = null;

  private readonly DEV_ALL_ACCESS = false;

  constructor(private authService: AuthService) {}

  private readonly LOTTERY_PATHS = new Set([
    '/overview',
    '/transactional-accounts',
    '/operation-indicators',
    '/product',
    '/licensing',
    '/operators',
    '/users-indicators',
    '/player-list',
    '/monitoring',
    '/finance-indicators',
    '/operator-list',
    '/financial-summary',
    '/bet-summary',
    '/raffle-summary',
    '/user-config-lot',
    '/activity-log',
  ]);

  private readonly OPERATOR_PATHS = new Set([
    '/reports',
    '/sub-accounts',
    '/extrato',
    '/resumo',
    '/infracoes',
    '/comissoes',
    '/carteira-comissoes',
    '/deposito',
    '/depositoPix',
    '/cashIn',
    '/transferencia',
    '/saque-copia-cola',
    '/saque-boleto',
    '/transfer-balance',
    '/approve-withdraw',
    '/reserva-financeira',
    '/criptomoeda',
    '/wallet',
  ]);

  private readonly CONFIG_PATHS = new Set([
    '/account/view',
    '/webhook',
    '/api-credentials',
    '/user-config',
    '/banking-credentials',
    '/technical-events',
    '/checkout-config',
  ]);

  private readonly LOTTERY_PERMISSION_MAP: Record<string, string> = {
    '/overview': 'overview',
    '/transactional-accounts': 'transactionalAccounts',
    '/operation-indicators': 'operationIndicators',
    '/product': 'product',
    '/operators': 'operators',
    '/users-indicators': 'usersIndicators',
    '/player-list': 'playerList',
    '/monitoring': 'monitoring',
    '/finance-indicators': 'financeIndicators',
    '/operator-list': 'operatorList',
    '/financial-summary': 'financialSummary',
    '/bet-summary': 'betSummary',
    '/raffle-summary': 'raffleSummary',
    '/user-config-lot': 'userConfigLot',
    '/activity-log': 'activityLog',
  };

  private readonly OPERATOR_PERMISSION_MAP: Record<string, string> = {
    '/reports': 'reports',
    '/sub-accounts': 'subAccounts',
    '/extrato': 'extrato',
    '/resumo': 'resumo',
    '/infracoes': 'infracoes',
    '/comissoes': 'comissoes',
    '/carteira-comissoes': 'carteiraComissoes',
    '/deposito': 'deposito',
    '/depositoPix': 'depositoPix',
    '/cashIn': 'deposito',
    '/transferencia': 'transferencia',
    '/saque-copia-cola': 'saqueCopiaCola',
    '/saque-boleto': 'saqueBoleto',
    '/transfer-balance': 'transferBalance',
    '/approve-withdraw': 'approveWithdraw',
    '/reserva-financeira': 'reservaFinanceira',
    '/criptomoeda': 'criptomoeda',
    '/wallet': 'wallet',
    '/account/view': 'accountView',
    '/webhook': 'webhook',
    '/api-credentials': 'apiCredentials',
    '/user-config': 'userConfig',
    '/banking-credentials': 'bankingCredentials',
    '/technical-events': 'technicalEvents',
    '/checkout-config': 'checkoutConfig',
  };

  getAccountCached(force = false): Promise<any> {
    if (!force && this.accountCache) {
      return Promise.resolve(this.accountCache);
    }

    if (!force && this.accountPromise) {
      return this.accountPromise;
    }

    this.accountPromise = lastValueFrom(this.authService.getAccount())
      .then((account) => {
        this.accountCache = account;
        this.accountPromise = null;
        return account;
      })
      .catch((err) => {
        this.accountPromise = null;
        throw err;
      });

    return this.accountPromise;
  }

  clearCache(): void {
    this.accountCache = null;
    this.accountPromise = null;
  }

  isLotteryManager(account: any): boolean {
    return !!account?.accountLottery;
  }

  private normalizePath(path: string): string {
    return (path || '').split('?')[0].split('#')[0];
  }

  private getLotteryPermissions(): PermissionBag {
    return this.authService.getTokenLotteryPermissions?.() ?? {};
  }

  private getOperatorPermissions(): PermissionBag {
    return this.authService.getTokenPermissions?.() ?? {};
  }

  private hasAnyPermissionConfigured(permissions: PermissionBag): boolean {
    return Object.keys(permissions).length > 0;
  }

  private getPermissionValue(path: string): boolean {
    const normalized = this.normalizePath(path);

    if (normalized === '/licensing') {
      return true;
    }

    const lotteryKey = this.LOTTERY_PERMISSION_MAP[normalized];
    if (lotteryKey) {
      const permissionsLottery = this.getLotteryPermissions();

      if (!this.hasAnyPermissionConfigured(permissionsLottery)) {
        return false;
      }

      return permissionsLottery[lotteryKey] === true;
    }

    const operatorKey = this.OPERATOR_PERMISSION_MAP[normalized];
    if (operatorKey) {
      const permissions = this.getOperatorPermissions();

      if (!this.hasAnyPermissionConfigured(permissions)) {
        return false;
      }

      return permissions[operatorKey] === true;
    }

    return false;
  }

  async canAccessPath(path: string): Promise<boolean> {
    if (this.DEV_ALL_ACCESS) {
      return true;
    }

    const account = await this.getAccountCached();
    const normalized = this.normalizePath(path);
    const isLottery = this.isLotteryManager(account);

    if (isLottery) {
      if (!this.LOTTERY_PATHS.has(normalized)) {
        return false;
      }

      return this.getPermissionValue(normalized);
    }

    if (this.OPERATOR_PATHS.has(normalized) || this.CONFIG_PATHS.has(normalized)) {
      return this.getPermissionValue(normalized);
    }

    return false;
  }

  async getGroupHomeUrl(): Promise<string> {
    if (this.DEV_ALL_ACCESS) {
      return '/overview';
    }

    const account = await this.getAccountCached();
    const isLottery = this.isLotteryManager(account);

    const paths = isLottery
      ? Array.from(this.LOTTERY_PATHS)
      : [...Array.from(this.OPERATOR_PATHS), ...Array.from(this.CONFIG_PATHS)];

    for (const path of paths) {
      if (this.getPermissionValue(path)) {
        return path;
      }
    }

    return isLottery ? '/overview' : '/reports';
  }

  async getDeniedRedirectUrl(): Promise<string> {
    return this.getGroupHomeUrl();
  }

  async applyMenuRules(menu: any[]): Promise<any[]> {
    if (this.DEV_ALL_ACCESS) {
      return menu.map((item: any) => ({
        ...item,
        locked: false,
        children: item.children?.map((child: any) => ({
          ...child,
          locked: false,
        })),
      }));
    }

    const account = await this.getAccountCached();
    const isLottery = this.isLotteryManager(account);

    const allowedHeadTitles = isLottery
      ? new Set(['common.lotteryManagement', 'common.administration'])
      : new Set(['common.lotteryOperator', 'common.configurations']);

    const result: any[] = [];
    let currentSection: any[] = [];

    const flushSection = () => {
      if (currentSection.length > 1) {
        result.push(...currentSection);
      }
      currentSection = [];
    };

    for (const item of menu) {
      if (item.headTitle) {
        flushSection();

        if (allowedHeadTitles.has(item.headTitle)) {
          currentSection = [item];
        } else {
          currentSection = [];
        }

        continue;
      }

      if (!currentSection.length) continue;
      currentSection.push(this.applyLockedRecursive(item));
    }

    flushSection();
    return result;
  }

  private applyLockedRecursive(item: any): any {
    const clone = {
      ...item,
      children: item.children
        ? item.children.map((c: any) => this.applyLockedRecursive(c))
        : undefined,
    };

    if (clone.path) {
      const normalized = this.normalizePath(clone.path);
      const allowed = this.getPermissionValue(clone.path);
      clone.locked = !!clone.locked || !allowed;
    }

    if (clone.children?.length) {
      const allChildrenLocked = clone.children.every((child: any) => !!child.locked);
      if (allChildrenLocked) {
        clone.locked = true;
      }
    }

    return clone;
  }
}